/**
 * Which movement the theatre's ← and → step to, over the list the trainer is actually looking at.
 *
 * **The list, not the catalogue.** The walk runs over the rows the table is drawing — filtered by
 * the search box, the facets and the scope, and in whatever order the trainer put the columns in.
 * A walk over the catalogue's own order would step from the movement on screen to one the table is
 * not showing, and the row highlighted behind the panel would be somewhere else entirely.
 *
 * ── It WRAPS, where the table's arrows CLAMP ────────────────────────────────────────────────
 * `usePointedRow.step` clamps: the last row of a four-hundred-row table stays the last row, because
 * a list that jumped back to the top would read as having scrolled on its own. The theatre is the
 * opposite case — it draws its two neighbours BY NAME, on 48px squares outside the panel, so a
 * clamped end would be a control captioned with the movement already on screen. The canon's own
 * specimen wraps (`LIST[i - 1] || LIST[LIST.length - 1]`), and the names under the squares are what
 * make the wrap legible rather than surprising.
 *
 * ── Two `null`s, and they are the same answer to two different questions ────────────────────
 * · **The list holds one movement.** Both neighbours would be the subject itself, and a pair of
 *   squares captioned with the name already on the bar is a control that does nothing while
 *   claiming to.
 * · **The subject is no longer in the list.** A filter typed while the theatre is open, a scope
 *   switch, a sort that ran — the address still names a movement and the theatre still draws it,
 *   because closing a panel the trainer is reading is a worse answer than leaving it open. But
 *   there is nowhere to step to FROM it: it has no position in the list any more, so it has no
 *   neighbours, and the walk is drawn as absent rather than guessing at an index.
 *
 * Takes the narrowest shape it can read rather than `ExerciseRow`: an id and a name is the whole
 * question, and a signature that named the projection would make this untestable without building
 * one.
 */

/** The least a walk needs to know about a movement. `ExerciseRow` satisfies it structurally. */
export interface TheatreStep {
  readonly id: number;
  readonly name: string;
}

export interface TheatreNeighbours {
  readonly previous: TheatreStep;
  readonly next: TheatreStep;
}

/**
 * The movements either side of `id` in `rows`, wrapping at both ends.
 *
 * `null` when the walk has nowhere to go — see the header for the two ways that happens.
 */
export function neighboursOf(
  rows: readonly TheatreStep[],
  id: number,
): TheatreNeighbours | null {
  if (rows.length < 2) return null;

  const at = rows.findIndex((row) => row.id === id);
  if (at < 0) return null;

  // Modular rather than `?? rows.at(-1)`: one expression for both ends, and the wrap is the
  // arithmetic rather than a fallback that has to be read as one.
  const previous = rows[(at - 1 + rows.length) % rows.length];
  const next = rows[(at + 1) % rows.length];
  if (previous === undefined || next === undefined) return null;

  return { previous, next };
}

/**
 * The row focus lands on when the movement a card was about is DELETED: the one after it, or — when
 * it was the last row — the one before it. Never the wrap: after the last row the walk's `next` is
 * the FIRST row, and landing at the top of a long list is a place the trainer did not come from.
 * `null` when nothing else is on the list, or when the movement is not on it.
 */
export function neighbourAfterRemoval(rows: readonly TheatreStep[], id: number): TheatreStep | null {
  const at = rows.findIndex((row) => row.id === id);
  if (at < 0) return null;
  return rows[at + 1] ?? rows[at - 1] ?? null;
}
